import { CSSProperties } from "react";
import {
  Game,
  GameInfoEvent,
  Player,
  battleCardsDefRepository,
  getGameEventInfoArg,
} from "~/business";
import { Colors, Sizes } from "~/jsx/theme";
import { expectNever, ordinal } from "~/utils";
import { XActionSlot } from "./XActionSlot";
import { XAgentEffect } from "./XAgentEffect";
import { XFactionChange } from "./XFactionChange";
import { XPlayerIcon } from "./XPlayerIcon";
import { XPlayerName } from "./XPlayerName";
import { XPlayerTroop } from "./XPlayerTroop";
import { XResourceWithAmount } from "./XResourceWithAmount";
import { XCard } from "./XCard";

export const XGameEventInfo = ({
  event,
  game,
}: {
  event: GameInfoEvent;
  game: Game;
}) => {
  const iconStyle: CSSProperties = {
    display: "inline-block",
    verticalAlign: "middle",
  };
  const iconWidth = 24;
  const { methodName } = event;
  switch (methodName) {
    case "conflictWon": {
      const { combatPodium = [], currentBattleCard } = game;
      if (!combatPodium.length) return null;
      const { effects } = currentBattleCard.getDef(battleCardsDefRepository);
      return (
        <>
          Conflict is over.
          <br />
          {combatPodium.flatMap(({ group, index: podiumPlace }) => {
            return group.map((player) => (
              <div key={player.id}>
                <b>{ordinal(podiumPlace)}:</b>{" "}
                <XResourceWithAmount
                  amount={player.combatPoints}
                  type="combatPoint"
                  width={18}
                  variant="compact"
                  style={{ display: "inline-block", verticalAlign: "middle" }}
                />
                <XPlayerIconAndName player={player} /> receives{" "}
                <span>
                  {effects[podiumPlace - 1].map((effect, effectIndex) => (
                    <XAgentEffect
                      key={effectIndex}
                      context="card"
                      effectDescription={effect}
                      width={iconWidth}
                      layout="horizontal"
                      style={iconStyle}
                      color={Colors.common.black}
                    />
                  ))}
                </span>
              </div>
            ));
          })}
        </>
      );
    }
    case "factionBonus": {
      const { effect, faction } = getGameEventInfoArg<typeof methodName>(event);
      return (
        <>
          goes beyond level 4 with faction{" "}
          <XFactionChange
            change={0}
            width={iconWidth}
            faction={faction}
            style={iconStyle}
          />{" "}
          and receives{" "}
          <XAgentEffect
            effectDescription={effect}
            context="card"
            width={iconWidth}
            style={iconStyle}
          />{" "}
        </>
      );
    }
    case "freeControlTroop": {
      const { slotId, playerId } =
        getGameEventInfoArg<typeof methodName>(event);
      const actionSlot = game.getActionSlot(slotId);
      return (
        <>
          receives a free{" "}
          <XPlayerTroop
            width={iconWidth}
            playerId={playerId}
            style={iconStyle}
          />{" "}
          in the conflict for the control of{" "}
          <XActionSlot
            actionSlot={actionSlot}
            style={iconStyle}
            noAnimation={true}
          />
        </>
      );
    }
    case "controlBonus": {
      const { slotId, effect } = getGameEventInfoArg<typeof methodName>(event);
      const actionSlot = game.getActionSlot(slotId);
      return (
        <>
          receives{" "}
          <XAgentEffect
            effectDescription={effect}
            width={iconWidth}
            context="actionSlot"
            style={iconStyle}
          />{" "}
          for control of{" "}
          <XActionSlot
            actionSlot={actionSlot}
            style={iconStyle}
            noAnimation={true}
          />
        </>
      );
    }
    case "stealIntrigueCard": {
      const { fromPlayerId, toPlayerId } =
        getGameEventInfoArg<typeof methodName>(event);
      const fromPlayer = game.getPlayer(fromPlayerId);
      const toPlayer = game.getPlayer(toPlayerId);
      return (
        <>
          <XPlayerIconAndName player={toPlayer} /> stole an intrigue card from{" "}
          <XPlayerIconAndName player={fromPlayer} />
        </>
      );
    }
    case "revealAll":
      const { playerId } = getGameEventInfoArg<typeof methodName>(event);
      const player = game.getPlayer(playerId);
      const { revealZone } = player;
      return (
        <>
          <XPlayerIconAndName player={player} /> revealed his hand
          <br />
          <div style={{ display: "flex", gap: 10 }}>
            {revealZone.pile.map((card) => (
              <XCard
                key={card.id}
                card={card}
                removeAnimationId={true}
                width={Sizes.cardWidths.small}
              />
            ))}
          </div>
        </>
      );
    case "endBuyPhase":
      return null;
    default: {
      expectNever(methodName);
      throw new Error();
    }
  }
};

const XPlayerIconAndName = ({ player }: { player: Player }) => {
  return (
    <>
      <XPlayerIcon width={18} playerId={player.id} />{" "}
      <XPlayerName name={player.name} />
    </>
  );
};
